/**
 * Calculadora de bomba — registry de fármacos.
 *
 * Cada entrada describe el rango de dosis típico, las preparaciones más
 * habituales (concentración) y el tipo de unidad (que determina la fórmula).
 * Los valores se extraen de la ficha clínica validada del fármaco, no se
 * inventan en código.
 *
 * tipo:
 *   "mcg-kg-min" → mL/h = (peso × dosis × 60) / conc(mcg/mL)
 *   "mcg-kg-h"   → mL/h = (peso × dosis) / conc(mcg/mL)
 *   "mg-kg-h"    → mL/h = (peso × dosis) / conc(mg/mL)
 *   "mg-kg-min"  → mL/h = (peso × dosis × 60) / conc(mg/mL)
 *   "mcg-min"    → mL/h = (dosis × 60) / conc(mcg/mL)   (sin peso)
 *   "mg-min"     → mL/h = (dosis × 60) / conc(mg/mL)    (sin peso)
 *   "mg-h"       → mL/h = dosis / conc(mg/mL)           (sin peso)
 *   "mcg-h"      → mL/h = dosis / conc(mcg/mL)          (sin peso)
 *   "ui-kg-h"    → mL/h = (peso × dosis) / conc(UI/mL)
 *   "ui-h"       → mL/h = dosis / conc(UI/mL)            (sin peso)
 *   "ui-min"     → mL/h = (dosis × 60) / conc(UI/mL)     (sin peso)
 */
export const CALCULADORAS = {
  // Vasoactivos en mcg/kg/min
  noradrenalina: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.01, max: 2, step: 0.01, unidad: "mcg/kg/min" },
    rangoHabitual: "0,05 – 0,5 mcg/kg/min",
    presets: [
      { label: "VVC · 8 mg en 100 mL", subtitle: "80 mcg/mL", valor: 80 },
      { label: "VVC · 8 mg en 250 mL", subtitle: "32 mcg/mL", valor: 32 },
      { label: "VVP · 4 mg en 250 mL", subtitle: "16 mcg/mL", valor: 16 },
    ],
  },
  adrenalina: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.01, max: 1, step: 0.01, unidad: "mcg/kg/min" },
    rangoHabitual: "0,05 – 0,5 mcg/kg/min",
    presets: [
      { label: "Estándar · 4 mg en 250 mL", subtitle: "16 mcg/mL", valor: 16 },
      { label: "Bomba jeringa · 4 mg en 50 mL", subtitle: "80 mcg/mL", valor: 80 },
    ],
  },
  dopamina: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 1, max: 20, step: 0.5, unidad: "mcg/kg/min" },
    rangoHabitual: "2 – 20 mcg/kg/min (inotrópico 5–10, vasopresor 10–20)",
    presets: [
      { label: "Estándar UCI · 200 mg en 250 mL", subtitle: "800 mcg/mL", valor: 800 },
      { label: "Concentrada VVC · 400 mg en 250 mL", subtitle: "1600 mcg/mL", valor: 1600 },
      { label: "Bomba jeringa · 200 mg en 50 mL", subtitle: "4000 mcg/mL", valor: 4000 },
    ],
  },
  dobutamina: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 1, max: 20, step: 0.5, unidad: "mcg/kg/min" },
    rangoHabitual: "5 – 15 mcg/kg/min",
    presets: [
      { label: "Estándar UCI · 250 mg en 250 mL", subtitle: "1000 mcg/mL", valor: 1000 },
      { label: "Concentrada VVC · 500 mg en 250 mL", subtitle: "2000 mcg/mL", valor: 2000 },
    ],
  },
  fenilefrina: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.1, max: 5, step: 0.1, unidad: "mcg/kg/min" },
    rangoHabitual: "0,5 – 5 mcg/kg/min",
    presets: [
      { label: "Infusión · 10 mg en 100 mL", subtitle: "100 mcg/mL", valor: 100 },
      { label: "Infusión diluida · 10 mg en 250 mL", subtitle: "40 mcg/mL", valor: 40 },
    ],
  },
  // Sedoanalgesia en mg/kg/h
  propofol: {
    tipo: "mg-kg-h",
    rangoDosis: { min: 0.3, max: 4, step: 0.1, unidad: "mg/kg/h" },
    rangoHabitual: "0,3 – 3 mg/kg/h (status 2 – 10)",
    presets: [
      { label: "Sin diluir · vial 10 mg/mL", subtitle: "10 mg/mL", valor: 10 },
    ],
  },
  midazolam: {
    tipo: "mg-kg-h",
    rangoDosis: { min: 0.02, max: 0.2, step: 0.01, unidad: "mg/kg/h" },
    rangoHabitual: "0,02 – 0,2 mg/kg/h",
    presets: [
      { label: "Infusión UCI · 50 mg en 100 mL", subtitle: "0,5 mg/mL", valor: 0.5 },
      { label: "Bomba jeringa · 100 mg en 100 mL", subtitle: "1 mg/mL", valor: 1 },
    ],
  },
  ketamina: {
    tipo: "mg-kg-h",
    rangoDosis: { min: 0.1, max: 2, step: 0.1, unidad: "mg/kg/h" },
    rangoHabitual: "0,2 – 1 mg/kg/h (analgesia 0,1 – 0,3)",
    presets: [
      { label: "Sedación UCI · 500 mg en 100 mL", subtitle: "5 mg/mL", valor: 5 },
      { label: "Bomba jeringa · 200 mg en 50 mL", subtitle: "4 mg/mL", valor: 4 },
      { label: "Analgesia · 100 mg en 100 mL", subtitle: "1 mg/mL", valor: 1 },
    ],
  },
  esketamina: {
    tipo: "mg-kg-h",
    rangoDosis: { min: 0.1, max: 1, step: 0.05, unidad: "mg/kg/h" },
    rangoHabitual: "0,1 – 0,5 mg/kg/h",
    presets: [
      { label: "Infusión · 25 mg en 50 mL", subtitle: "0,5 mg/mL", valor: 0.5 },
      { label: "Infusión · 125 mg en 100 mL", subtitle: "1,25 mg/mL", valor: 1.25 },
    ],
  },
  rocuronio: {
    tipo: "mg-kg-h",
    rangoDosis: { min: 0.3, max: 0.6, step: 0.05, unidad: "mg/kg/h" },
    rangoHabitual: "0,3 – 0,6 mg/kg/h (UCI titulado por TOF)",
    presets: [
      { label: "Bomba jeringa · 50 mg en 50 mL", subtitle: "1 mg/mL", valor: 1 },
      { label: "Volumétrica · 100 mg en 100 mL", subtitle: "1 mg/mL", valor: 1 },
    ],
  },
  // Opioide en mcg/kg/h
  fentanilo: {
    tipo: "mcg-kg-h",
    rangoDosis: { min: 0.5, max: 10, step: 0.1, unidad: "mcg/kg/h" },
    rangoHabitual: "0,7 – 10 mcg/kg/h (titular por EVA y RASS)",
    presets: [
      { label: "Infusión UCI · 1 mg en 100 mL", subtitle: "10 mcg/mL", valor: 10 },
      { label: "Bomba jeringa · 2,5 mg en 50 mL", subtitle: "50 mcg/mL", valor: 50 },
    ],
  },
  remifentanilo: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.02, max: 1, step: 0.01, unidad: "mcg/kg/min" },
    rangoHabitual: "0,05 – 0,5 mcg/kg/min (analgesia 0,05 – 0,15)",
    presets: [
      { label: "Reconstitución · 2 mg en 40 mL", subtitle: "50 mcg/mL", valor: 50 },
      { label: "Dosis bajas · 1 mg en 50 mL", subtitle: "20 mcg/mL", valor: 20 },
      { label: "Bomba jeringa · 2 mg en 50 mL", subtitle: "40 mcg/mL", valor: 40 },
    ],
  },
  // Sedación en mcg/kg/h
  dexmedetomidina: {
    tipo: "mcg-kg-h",
    rangoDosis: { min: 0.1, max: 1.4, step: 0.05, unidad: "mcg/kg/h" },
    rangoHabitual: "0,4 – 0,8 mcg/kg/h (titular por RASS)",
    presets: [
      { label: "Estándar · 200 mcg en 50 mL", subtitle: "4 mcg/mL", valor: 4 },
      { label: "Volumétrica · 400 mcg en 100 mL", subtitle: "4 mcg/mL", valor: 4 },
    ],
  },
  // Barbitúrico en mg/kg/h
  tiopental: {
    tipo: "mg-kg-h",
    rangoDosis: { min: 1, max: 10, step: 0.5, unidad: "mg/kg/h" },
    rangoHabitual: "1 – 5 mg/kg/h (status); coma barbitúrico hasta 10",
    presets: [
      { label: "Reconstitución · 500 mg en 20 mL", subtitle: "25 mg/mL", valor: 25 },
      { label: "Infusión sostenida · 1 g en 100 mL", subtitle: "10 mg/mL", valor: 10 },
    ],
  },
  // BNM en mcg/kg/min
  vecuronio: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.5, max: 2, step: 0.1, unidad: "mcg/kg/min" },
    rangoHabitual: "0,8 – 1,7 mcg/kg/min",
    presets: [
      { label: "Volumétrica · 20 mg en 100 mL", subtitle: "200 mcg/mL", valor: 200 },
      { label: "Bomba jeringa · 10 mg en 50 mL", subtitle: "200 mcg/mL", valor: 200 },
    ],
  },
  cisatracurio: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.5, max: 3, step: 0.1, unidad: "mcg/kg/min" },
    rangoHabitual: "1 – 3 mcg/kg/min (UCI titulado por TOF)",
    presets: [
      { label: "Bomba jeringa · 20 mg en 50 mL", subtitle: "400 mcg/mL", valor: 400 },
      { label: "Volumétrica · 150 mg en 250 mL", subtitle: "600 mcg/mL", valor: 600 },
    ],
  },
  atracurio: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 2, max: 12, step: 0.5, unidad: "mcg/kg/min" },
    rangoHabitual: "5 – 10 mcg/kg/min",
    presets: [
      { label: "Bomba jeringa · 50 mg en 50 mL", subtitle: "1000 mcg/mL", valor: 1000 },
      { label: "Volumétrica · 250 mg en 250 mL", subtitle: "1000 mcg/mL", valor: 1000 },
    ],
  },
  // Vasodilatadores en mcg/kg/min
  "nitroprusiato-de-sodio": {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.1, max: 5, step: 0.1, unidad: "mcg/kg/min" },
    rangoHabitual: "0,5 – 3 mcg/kg/min",
    presets: [
      { label: "Estándar UCI · 50 mg en 250 mL", subtitle: "200 mcg/mL", valor: 200 },
      { label: "Concentrada · 50 mg en 100 mL", subtitle: "500 mcg/mL", valor: 500 },
    ],
  },
  // Infusiones sin peso (-min y -h sin kg)
  nitroglicerina: {
    tipo: "mcg-min",
    rangoDosis: { min: 5, max: 400, step: 5, unidad: "mcg/min" },
    rangoHabitual: "10 – 200 mcg/min (crisis HTA hasta 400)",
    presets: [
      { label: "Estándar UCI · 50 mg en 250 mL", subtitle: "200 mcg/mL", valor: 200 },
      { label: "Concentrada · 100 mg en 250 mL", subtitle: "400 mcg/mL", valor: 400 },
      { label: "Bomba jeringa · 50 mg en 50 mL", subtitle: "1000 mcg/mL", valor: 1000 },
    ],
  },
  lidocaina: {
    tipo: "mg-min",
    rangoDosis: { min: 1, max: 4, step: 0.5, unidad: "mg/min" },
    rangoHabitual: "1 – 4 mg/min (antiarrítmico mantenimiento)",
    presets: [
      { label: "Infusión UCI · 1 g en 250 mL", subtitle: "4 mg/mL", valor: 4 },
    ],
  },
  morfina: {
    tipo: "mg-h",
    rangoDosis: { min: 0.5, max: 5, step: 0.1, unidad: "mg/h" },
    rangoHabitual: "0,8 – 3 mg/h (titular por EVA, no por peso)",
    presets: [
      { label: "Bomba jeringa · 50 mg en 50 mL", subtitle: "1 mg/mL", valor: 1 },
      { label: "Volumétrica · 50 mg en 100 mL", subtitle: "0,5 mg/mL", valor: 0.5 },
    ],
  },
  // Diurético en mg/h sin peso
  furosemida: {
    tipo: "mg-h",
    rangoDosis: { min: 5, max: 40, step: 1, unidad: "mg/h" },
    rangoHabitual: "5 – 40 mg/h (titulado por respuesta diurética)",
    presets: [
      { label: "Infusión continua · 250 mg en 250 mL", subtitle: "1 mg/mL", valor: 1 },
    ],
  },
  // IBP infusión continua para HDA (mg/h sin peso)
  omeprazol: {
    tipo: "mg-h",
    rangoDosis: { min: 4, max: 12, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "8 mg/h (HDA × 72 h, tras bolo 80 mg)",
    presets: [
      { label: "Estándar HDA · 80 mg en 100 mL", subtitle: "0,8 mg/mL", valor: 0.8 },
      { label: "Diluida · 40 mg en 100 mL", subtitle: "0,4 mg/mL", valor: 0.4 },
    ],
  },
  pantoprazol: {
    tipo: "mg-h",
    rangoDosis: { min: 4, max: 12, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "8 mg/h (HDA × 72 h, tras bolo 80 mg)",
    presets: [
      { label: "Estándar HDA · 80 mg en 100 mL", subtitle: "0,8 mg/mL", valor: 0.8 },
      { label: "Diluida · 40 mg en 100 mL", subtitle: "0,4 mg/mL", valor: 0.4 },
    ],
  },
  esomeprazol: {
    tipo: "mg-h",
    rangoDosis: { min: 4, max: 12, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "8 mg/h (HDA × 72 h, tras bolo 80 mg)",
    presets: [
      { label: "Estándar HDA · 80 mg en 100 mL", subtitle: "0,8 mg/mL", valor: 0.8 },
    ],
  },
  // Anticoagulante en UI/kg/h
  heparina: {
    tipo: "ui-kg-h",
    rangoDosis: { min: 5, max: 30, step: 1, unidad: "UI/kg/h" },
    rangoHabitual: "18 UI/kg/h inicial · titular por TTPK 1,5–2,5×",
    presets: [
      { label: "Estándar UCI · 25.000 UI en 250 mL", subtitle: "100 UI/mL", valor: 100 },
      { label: "Alternativa · 25.000 UI en 500 mL", subtitle: "50 UI/mL", valor: 50 },
    ],
  },
  "insulina-regular": {
    tipo: "ui-kg-h",
    rangoDosis: { min: 0.05, max: 0.5, step: 0.01, unidad: "UI/kg/h" },
    rangoHabitual: "0,1 UI/kg/h (cetoacidosis) · titular por glicemia",
    presets: [
      { label: "Infusión continua · 100 UI en 100 mL SF", subtitle: "1 UI/mL", valor: 1 },
    ],
  },
  // β-bloqueador ultracorto (mcg/kg/min)
  esmolol: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 25, max: 200, step: 5, unidad: "mcg/kg/min" },
    rangoHabitual: "50 – 200 mcg/kg/min (tras bolo 500 mcg/kg)",
    presets: [
      { label: "Diluida · 2,5 g en 250 mL", subtitle: "10 mg/mL = 10.000 mcg/mL", valor: 10000 },
      { label: "Premezcla · ampolla 10 mg/mL", subtitle: "10.000 mcg/mL", valor: 10000 },
    ],
  },
  // Inotrópico (mcg/kg/min)
  milrinona: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.1, max: 0.75, step: 0.05, unidad: "mcg/kg/min" },
    rangoHabitual: "0,375 – 0,5 mcg/kg/min",
    presets: [
      { label: "Estándar · 20 mg en 100 mL", subtitle: "200 mcg/mL", valor: 200 },
      { label: "Concentrada VVC · 50 mg en 100 mL", subtitle: "500 mcg/mL", valor: 500 },
      { label: "Bomba jeringa · 20 mg en 50 mL", subtitle: "400 mcg/mL", valor: 400 },
    ],
  },
  levosimendan: {
    tipo: "mcg-kg-min",
    rangoDosis: { min: 0.05, max: 0.2, step: 0.01, unidad: "mcg/kg/min" },
    rangoHabitual: "0,1 mcg/kg/min × 24 h (sin carga rutinaria)",
    presets: [
      { label: "Estándar · 12,5 mg en 500 mL SG 5%", subtitle: "25 mcg/mL", valor: 25 },
      { label: "Concentrada · 25 mg en 500 mL", subtitle: "50 mcg/mL", valor: 50 },
    ],
  },
  // Antihipertensivos en infusión (mg/h sin peso)
  nicardipino: {
    tipo: "mg-h",
    rangoDosis: { min: 2.5, max: 15, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "5 – 15 mg/h (titulación de PA)",
    presets: [
      { label: "Estándar · 25 mg en 250 mL", subtitle: "0,1 mg/mL", valor: 0.1 },
      { label: "Concentrada VVC · 25 mg en 100 mL", subtitle: "0,25 mg/mL", valor: 0.25 },
    ],
  },
  diltiazem: {
    tipo: "mg-h",
    rangoDosis: { min: 5, max: 15, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "5 – 15 mg/h (titulado por FC y PA)",
    presets: [
      { label: "Estándar · 100 mg en 100 mL", subtitle: "1 mg/mL", valor: 1 },
    ],
  },
  // Antihipertensivo + antiarrítmico (mg/min sin peso)
  labetalol: {
    tipo: "mg-min",
    rangoDosis: { min: 0.5, max: 2, step: 0.1, unidad: "mg/min" },
    rangoHabitual: "0,5 – 2 mg/min (típico 1 mg/min)",
    presets: [
      { label: "Estándar · 200 mg en 200 mL", subtitle: "1 mg/mL", valor: 1 },
    ],
  },
  // Antiarrítmico clase III (mg/min sin peso)
  amiodarona: {
    tipo: "mg-min",
    rangoDosis: { min: 0.25, max: 1, step: 0.05, unidad: "mg/min" },
    rangoHabitual: "1 mg/min × 6 h, luego 0,5 mg/min × 18 h",
    presets: [
      { label: "Mantenimiento · 900 mg en 500 mL SG 5%", subtitle: "1,8 mg/mL", valor: 1.8 },
      { label: "Carga · 150 mg en 100 mL", subtitle: "1,5 mg/mL", valor: 1.5 },
    ],
  },
  // Eclampsia (mg/h sin peso - usuario ingresa mg directos)
  "sulfato-de-magnesio": {
    tipo: "mg-h",
    rangoDosis: { min: 1000, max: 2000, step: 100, unidad: "mg/h" },
    rangoHabitual: "1000 – 2000 mg/h (= 1 – 2 g/h en eclampsia)",
    presets: [
      { label: "Eclampsia · 10 g en 250 mL SG 5%", subtitle: "40 mg/mL", valor: 40 },
    ],
  },
  // Vasoactivo no-catecolamínico (UI/min sin peso)
  vasopresina: {
    tipo: "ui-min",
    rangoDosis: { min: 0.01, max: 0.07, step: 0.005, unidad: "UI/min" },
    rangoHabitual: "0,03 – 0,04 UI/min (máx 0,07 UI/min ISP)",
    presets: [
      { label: "Estándar · 20 UI en 100 mL", subtitle: "0,2 UI/mL", valor: 0.2 },
      { label: "Bomba jeringa VVC · 20 UI en 50 mL", subtitle: "0,4 UI/mL", valor: 0.4 },
      { label: "ISP 0,1 UI/mL · 20 UI en 200 mL", subtitle: "0,1 UI/mL", valor: 0.1 },
    ],
  },
  // Antiepiléptico en infusión (mg/kg/h)
  "acido-valproico": {
    tipo: "mg-kg-h",
    rangoDosis: { min: 0.5, max: 3, step: 0.1, unidad: "mg/kg/h" },
    rangoHabitual: "1 – 2 mg/kg/h (status epiléptico mantenimiento)",
    presets: [
      { label: "Estándar · 1.000 mg en 100 mL SF", subtitle: "10 mg/mL", valor: 10 },
      { label: "Diluida · 2.000 mg en 250 mL", subtitle: "8 mg/mL", valor: 8 },
    ],
  },
  // α2 central en infusión sedación (mcg/kg/h)
  clonidina: {
    tipo: "mcg-kg-h",
    rangoDosis: { min: 0.3, max: 2, step: 0.1, unidad: "mcg/kg/h" },
    rangoHabitual: "0,5 – 2 mcg/kg/h (sedación UCI titulada por RASS)",
    presets: [
      { label: "Estándar · 600 mcg en 100 mL", subtitle: "6 mcg/mL", valor: 6 },
    ],
  },
  // Hormona contrainsular en infusión (mg/h sin peso, sobredosis BB)
  glucagon: {
    tipo: "mg-h",
    rangoDosis: { min: 2, max: 10, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "2 – 10 mg/h (sobredosis betabloqueador, tras bolo 3–10 mg)",
    presets: [
      { label: "Estándar · 10 mg en 100 mL SG 5%", subtitle: "0,1 mg/mL", valor: 0.1 },
      { label: "Concentrada · 10 mg en 50 mL", subtitle: "0,2 mg/mL", valor: 0.2 },
    ],
  },
  // Corticoide en infusión continua (mg/h sin peso, shock séptico)
  hidrocortisona: {
    tipo: "mg-h",
    rangoDosis: { min: 4, max: 12, step: 0.5, unidad: "mg/h" },
    rangoHabitual: "8 mg/h (= 200 mg/24 h, shock séptico refractario)",
    presets: [
      { label: "Shock séptico · 200 mg en 100 mL SF", subtitle: "2 mg/mL", valor: 2 },
    ],
  },
};

// Slugs de fármacos con preset de infusión, para alimentar el buscador global
// (cada uno deep-linkea a la calculadora con ese fármaco preseleccionado).
export const CALCULADORAS_SLUGS = Object.keys(CALCULADORAS);

export function tieneCalculadora(slug) {
  return Boolean(slug && CALCULADORAS[slug]);
}

export function usaPesoEnCalculo(tipo) {
  return tipo.includes("-kg-");
}

export function calcularMlPorHora(tipo, peso, dosis, conc) {
  if (!conc || conc <= 0) return 0;
  const factor = tipo.endsWith("-min") ? 60 : 1;
  const multPeso = usaPesoEnCalculo(tipo) ? peso : 1;
  return (multPeso * dosis * factor) / conc;
}

export function labelDosisTotal(tipo) {
  if (tipo === "mcg-kg-min" || tipo === "mcg-min") return "mcg/min";
  if (tipo === "mcg-kg-h" || tipo === "mcg-h") return "mcg/h";
  if (tipo === "mg-kg-min" || tipo === "mg-min") return "mg/min";
  if (tipo === "mg-kg-h" || tipo === "mg-h") return "mg/h";
  if (tipo === "ui-kg-h" || tipo === "ui-h") return "UI/h";
  if (tipo === "ui-min") return "UI/min";
  return "";
}

export function dosisTotal(tipo, peso, dosis) {
  return usaPesoEnCalculo(tipo) ? peso * dosis : dosis;
}
